import { useState } from 'react';
import { supabase } from '../../../services/supabaseClient';
import { generateSigePlanHazards } from '../../../services/geminiService';
import { AI_PROMPTS } from './ki-prompts';

// Erstes balanciertes JSON-Objekt/-Array aus dem Text ziehen —
// Gemini hängt teils Erläuterungstext hinter das JSON (Parse-Fehler "after position N")
const extractJson = (s: string): any => {
  const startO = s.indexOf('{');
  const startA = s.indexOf('[');
  const start = startO === -1 ? startA : (startA === -1 ? startO : Math.min(startO, startA));
  if (start === -1) throw new Error('Kein JSON in der KI-Antwort gefunden');
  const open = s[start];
  const close = open === '{' ? '}' : ']';
  let depth = 0, inStr = false, esc = false;
  for (let i = start; i < s.length; i++) {
    const ch = s[i];
    if (inStr) {
      if (esc) esc = false;
      else if (ch === '\\') esc = true;
      else if (ch === '"') inStr = false;
      continue;
    }
    if (ch === '"') inStr = true;
    else if (ch === open) depth++;
    else if (ch === close) {
      depth--;
      if (depth === 0) return JSON.parse(s.slice(start, i + 1));
    }
  }
  throw new Error('Unvollständiges JSON in der KI-Antwort');
};

/**
 * Ergebnis eines KI-Vorschlags. Ein Fehlschlag wird NICHT verschluckt: der
 * Aufrufer erfährt über `art`, ob der Dienst nicht erreichbar war
 * (`netzwerk`) oder ob die Antwort der KI nicht auswertbar war (`antwort`).
 * Früher kam in beiden Fällen `null` zurück und die Oberfläche meldete
 * pauschal „Keine KI-Vorschläge erhalten".
 */
export type GefaehrdungsVorschlagErgebnis =
  | { status: 'ok'; data: any }
  | { status: 'fehler'; art: 'netzwerk' | 'antwort'; fehler: string };

export function useGefaehrdungsVorschlag() {
  const [loading, setLoading] = useState(false);

  const suggestHazards = async (arbeitsschrittId: string): Promise<GefaehrdungsVorschlagErgebnis> => {
    setLoading(true);
    // Bis die KI-Antwort vorliegt, sind alle Fehler Transport-/Abfragefehler.
    let art: 'netzwerk' | 'antwort' = 'netzwerk';
    try {
      const { data: step, error: stepError } = await supabase.from('sige_arbeitsschritte').select('*').eq('id', arbeitsschrittId).single();
      if (stepError) throw new Error(`Arbeitsschritt konnte nicht gelesen werden: ${stepError.message}`);
      const { data: existGefs } = await supabase.from('sige_gefaehrdungen').select('bezeichnung').eq('arbeitsschritt_id', arbeitsschrittId);

      if (!step) throw new Error("Arbeitsschritt nicht gefunden");

      const existingStr = existGefs?.map(g => g.bezeichnung).join(', ') || 'Keine';
      
      // Ersatz als Funktion: ein „$&" oder „$'" in einer Bezeichnung würde als
      // Ersetzungsmuster ausgewertet und den Prompt verstümmeln.
      const promptText = AI_PROMPTS.GEFAEHRDUNGS_VORSCHLAG
        .replace('{bezeichnung}', () => step.bezeichnung)
        .replace('{gewerk}', () => step.gewerk || 'Unbekannt')
        .replace('{vorhandeneGefaehrdungen}', () => existingStr);

      const resultText = await generateSigePlanHazards(promptText);

      // Ab hier ist die Antwort da — Folgefehler sind Auswertungsfehler.
      art = 'antwort';

      let resultJson = resultText;

      if (resultJson && resultJson.text) resultJson = resultJson.text;
      if (resultJson && resultJson.content) resultJson = resultJson.content;

      if (typeof resultJson === 'string') {
        resultJson = extractJson(resultJson);
      }

      return { status: 'ok', data: resultJson };
    } catch (e: any) {
      console.error("Fehler beim Abrufen von KI-Vorschlägen:", e);
      return { status: 'fehler', art, fehler: e?.message || String(e) };
    } finally {
      setLoading(false);
    }
  };

  return { suggestHazards, loading };
}
